import { Injectable, OnDestroy, inject } from '@angular/core';
import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import { Category } from '@app/core/services/api/API.service';
import { ROLES_GROUPS } from '@app/constants';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { sortJSON } from '../../util.service';
import { distinctUntilChanged, take } from 'rxjs/operators';
import * as subscriptions from '@app/core/graphql/subscriptions';
import { AmplifyApiService } from '@app/core/api/services';
import { UserAuthService } from '@app/core/user/user.service';
import { FuseLoadingService } from '@fuse/services/loading';
import {
    BrandfactoryServerlessService,
    CategoryNode,
    CategoryNodeResponse
} from '../../serverless/brandfactory-serverless.service';
import { UserClientOptionService } from './user-client-option.service';
import {
    PremiumsCatalogLoadContext,
    ensurePremiumsClientContextLoaded,
    resolvePremiumsCatalogLoadContext
} from './premiums-catalog-load-context.util';

type PremiumCategoriesListRequestOptions = {
    clientId?: string;
    useAuth: boolean;
};

type PremiumCategoriesCascadeRequestOptions = PremiumCategoriesListRequestOptions & {
    cascade: true;
};

@Injectable({
    providedIn: 'root'
})
export class CategoriesService implements OnDestroy {
    private _categories$ = new BehaviorSubject<Category[]>([]);
    private _categoriesTree$ = new BehaviorSubject<CategoryNode[]>([]);
    private _categoriesTreeIndex$ = new BehaviorSubject<CategoryNode | null>(
        null
    );
    private _loading$ = new BehaviorSubject<boolean>(true);
    private categories: Category[] = [];
    private categoriesTree: CategoryNode[] = [];
    /** true cuando la última respuesta cascade vino como { data, index }. */
    private categoriesTreeResponseHasIndex = false;
    private subscriptions: any[] = [];
    private _amplifyApiService = inject(AmplifyApiService);
    private _brandfactoryServerlessService = inject(
        BrandfactoryServerlessService
    );
    private _userAuthService = inject(UserAuthService);
    private _fuseLoadingService = inject(FuseLoadingService);
    private _userClientOptionService = inject(UserClientOptionService);
    private _credentialsService = inject(CredentialsService);

    private _isLoadingTree = false;
    private _categoriesClientScope: string | null = null;
    private _lastTreeLoadAttempt = 0;
    private _maxRetries = 2;
    private _retryDelay = 500;
    private _isLoadingCategories = false;
    private _bootstrapPromise: Promise<void> | null = null;
    private _ensureInFlight: Promise<void> | null = null;
    private _reloadInFlight: Promise<void> | null = null;

    constructor() {
        this.subscriptions.push(
            this._userAuthService.user$
                .pipe(
                    distinctUntilChanged(
                        (prev, next) =>
                            (prev?.id ?? null) === (next?.id ?? null)
                    )
                )
                .subscribe(() => {
                    void this.reloadCategoriesForSession();
                })
        );
        this.subscriptions.push(
            combineLatest([
                this._userClientOptionService.client$,
                this._userClientOptionService.clientOption$
            ])
                .pipe(
                    distinctUntilChanged(
                        ([prevClient, prevOption], [nextClient, nextOption]) =>
                            prevClient?.id === nextClient?.id &&
                            prevOption?.restrictedCatalogue ===
                                nextOption?.restrictedCatalogue
                    )
                )
                .subscribe(([client, clientOption]) => {
                    const user = this._credentialsService
                        .credentialsCurrentUser;
                    if (
                        !user ||
                        !client ||
                        !clientOption ||
                        ROLES_GROUPS.PROVIDER_GROUP.includes(user.role)
                    ) {
                        return;
                    }
                    void this.reloadCategoriesForSession();
                })
        );
        this.subscriptions.push(
            this._amplifyApiService
                .subscribe({ query: subscriptions.onCreateCategory })
                .subscribe(observer => {
                    const data: any = observer.data.onCreateCategory;
                    this.categories.push(data);
                    this.categories = sortJSON(this.categories, 'name', 'asc');
                    this._categories$.next(this.categories);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onUpdateCategory })
                .subscribe(observer => {
                    const data: any = observer.data.onUpdateCategory;
                    this.categories = this.categories.map(
                        (category: Category) => {
                            return category.id === data.id ? data : category;
                        }
                    );
                    this.categories = sortJSON(
                        this.categories,
                        'comertial_name',
                        'asc'
                    );
                    this._categories$.next(this.categories);
                }),
            this._amplifyApiService
                .subscribe({ query: subscriptions.onDeleteCategory })
                .subscribe(observer => {
                    const data = observer.data.onDeleteCategory;
                    this.categories = this.categories.filter(
                        (category: Category) => {
                            return category.id !== data.id;
                        }
                    );
                    this._categories$.next(this.categories);
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get categories$() {
        return this._categories$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    get categoriesTree$() {
        return this._categoriesTree$.asObservable();
    }

    get categoriesTreeIndex$() {
        return this._categoriesTreeIndex$.asObservable();
    }

    getCategoriesSnapshot(): Category[] {
        return this._categories$.getValue() ?? [];
    }

    getCategoriesTreeSnapshot(): CategoryNode[] {
        return this._categoriesTree$.getValue() ?? [];
    }

    /** Espera el preload; recarga si cambió el scope de sesión (p. ej. login). */
    async ensurePreloaded(): Promise<void> {
        if (this._ensureInFlight) {
            return this._ensureInFlight;
        }

        this._ensureInFlight = this.ensurePreloadedInternal().finally(() => {
            this._ensureInFlight = null;
        });

        return this._ensureInFlight;
    }

    private async ensurePreloadedInternal(): Promise<void> {
        await this.waitForSessionUserIfNeeded();
        await this.waitForClientCatalogContextIfNeeded();
        const loadContext = await this.resolveCategoriesLoadContext();
        const scopeMatches =
            this._categoriesClientScope === loadContext.scopeKey;
        const hasCache =
            this.getCategoriesSnapshot().length > 0 &&
            this.getCategoriesTreeSnapshot().length > 0;

        if (hasCache && scopeMatches) {
            return;
        }

        if (!scopeMatches) {
            this._bootstrapPromise = null;
        }

        if (!this._bootstrapPromise) {
            const forceRefresh = hasCache && !scopeMatches;
            this._bootstrapPromise = this.runBootstrap(forceRefresh);
        }

        await this._bootstrapPromise;
    }

    clear(): void {
        this.categories = [];
        this.categoriesTree = [];
        this._categories$.next([]);
        this._categoriesTree$.next([]);
        this._categoriesTreeIndex$.next(null);
        this.categoriesTreeResponseHasIndex = false;
        this._loading$.next(false);
        this._isLoadingTree = false;
        this._isLoadingCategories = false;
        this._lastTreeLoadAttempt = 0;
        this._categoriesClientScope = null;
    }

    private async reloadCategoriesForSession(): Promise<void> {
        if (this._reloadInFlight) {
            return this._reloadInFlight;
        }

        this._reloadInFlight = this.reloadCategoriesForSessionInternal().finally(
            () => {
                this._reloadInFlight = null;
            }
        );

        return this._reloadInFlight;
    }

    private async reloadCategoriesForSessionInternal(): Promise<void> {
        await this.waitForSessionUserIfNeeded();
        await this.waitForClientCatalogContextIfNeeded();
        const loadContext = await this.resolveCategoriesLoadContext();
        const needsReload =
            this._categoriesClientScope === null ||
            this._categoriesClientScope !== loadContext.scopeKey;

        if (!needsReload) {
            return;
        }

        this._bootstrapPromise = null;
        await this.ensurePreloaded();
    }

    private async runBootstrap(forceRefresh = false): Promise<void> {
        await this.waitForSessionUserIfNeeded();
        await this.listCategories(forceRefresh);
        await this.listCategoriesTree(0, forceRefresh);
    }

    /** Evita la primera petición sin Authorization cuando hay credentials en localStorage. */
    private async waitForSessionUserIfNeeded(): Promise<void> {
        if (!localStorage.getItem('credentials')) {
            return;
        }
        if (this._credentialsService.credentialsCurrentUser) {
            return;
        }
        try {
            await firstValueFrom(this._userAuthService.user$.pipe(take(1)));
        } catch {
            // Continuar como invitado si la sesión no se resuelve.
        }
    }

    /** Espera client/clientOption antes de resolver scope MKT (evita doble list-premium-categories). */
    private async waitForClientCatalogContextIfNeeded(): Promise<void> {
        const user = this._credentialsService.credentialsCurrentUser;
        if (
            !user?.clientId ||
            !ROLES_GROUPS.MKT_GROUP.includes(user.role) ||
            ROLES_GROUPS.PROVIDER_GROUP.includes(user.role)
        ) {
            return;
        }

        await ensurePremiumsClientContextLoaded(
            user,
            this._userClientOptionService
        );
    }

    private buildPremiumCategoriesRequestOptions(
        loadContext: PremiumsCatalogLoadContext
    ): PremiumCategoriesListRequestOptions {
        return {
            ...(loadContext.clientId ? { clientId: loadContext.clientId } : {}),
            useAuth: loadContext.isAuthenticated
        };
    }

    private buildPremiumCategoriesCascadeRequestOptions(
        loadContext: PremiumsCatalogLoadContext
    ): PremiumCategoriesCascadeRequestOptions {
        return {
            ...this.buildPremiumCategoriesRequestOptions(loadContext),
            cascade: true
        };
    }

    private async resolveCategoriesLoadContext(): Promise<
        PremiumsCatalogLoadContext
    > {
        return resolvePremiumsCatalogLoadContext(
            this._credentialsService.credentialsCurrentUser,
            this._userClientOptionService
        );
    }

    private shouldUseCachedCategories(
        forceRefresh: boolean,
        scopeKey: string,
        hasCache: boolean
    ): boolean {
        return (
            !forceRefresh &&
            hasCache &&
            this._categoriesClientScope === scopeKey
        );
    }

    private filterRestrictedCategories(categories: Category[]): Category[] {
        return (categories || []).filter(category => !category?.hasRestricted);
    }

    private filterRestrictedCategoryTree(
        nodes: CategoryNode[],
        restrictedCategoryIds: Set<string>
    ): CategoryNode[] {
        if (!nodes?.length) {
            return [];
        }

        const result: CategoryNode[] = [];
        for (const node of nodes) {
            if (restrictedCategoryIds.has(node.id)) {
                continue;
            }

            const filteredChildren = this.filterRestrictedCategoryTree(
                node.children ?? [],
                restrictedCategoryIds
            );
            const isOriginallyLeaf = !node.children?.length;

            if (filteredChildren.length > 0 || isOriginallyLeaf) {
                result.push({
                    ...node,
                    children: filteredChildren
                });
            }
        }

        return result;
    }

    private getRestrictedCategoryIds(categories: Category[]): Set<string> {
        return new Set(
            (categories || [])
                .filter(category => category?.hasRestricted)
                .map(category => category.id)
        );
    }

    private applyRestrictedCategoryFilters(
        categories: Category[],
        tree: CategoryNode[],
        applyRestrictedFilter: boolean
    ): { categories: Category[]; tree: CategoryNode[] } {
        if (!applyRestrictedFilter) {
            return { categories, tree };
        }

        const filteredCategories = this.filterRestrictedCategories(categories);
        const restrictedIds = this.getRestrictedCategoryIds(categories);

        return {
            categories: filteredCategories,
            tree: this.filterRestrictedCategoryTree(tree, restrictedIds)
        };
    }

    async listCategories(forceRefresh: boolean = false) {
        if (this._isLoadingCategories && !forceRefresh) {
            if (this._bootstrapPromise) {
                await this._bootstrapPromise;
            }
            return;
        }

        if (forceRefresh) {
            this._isLoadingCategories = false;
        }

        const loadContext = await this.resolveCategoriesLoadContext();
        const existingCategories = this._categories$.getValue();
        if (
            this.shouldUseCachedCategories(
                forceRefresh,
                loadContext.scopeKey,
                !!existingCategories?.length
            )
        ) {
            this.categories =
                existingCategories ?? this.getCategoriesSnapshot();
            return;
        }

        this._categoriesClientScope = loadContext.scopeKey;
        this._isLoadingCategories = true;
        this._fuseLoadingService._setLoadingStatus(
            true,
            'premiums:categories:list'
        );
        try {
            this._loading$.next(true);
            const requestOptions = this.buildPremiumCategoriesRequestOptions(
                loadContext
            );
            const loadedCategories = await this._brandfactoryServerlessService.listPremiumCategories(
                requestOptions
            );
            this.categories = loadContext.applyRestrictedFilter
                ? this.filterRestrictedCategories(
                      (loadedCategories || []) as Category[]
                  )
                : ((loadedCategories || []) as Category[]);
        } catch (err) {
            this.categories = [];
        } finally {
            this._categories$.next(sortJSON(this.categories, 'name', 'asc'));
            this._loading$.next(false);
            this._isLoadingCategories = false;
            this._fuseLoadingService._setLoadingStatus(
                false,
                'premiums:categories:list'
            );
        }
    }

    async listCategoriesTree(
        retryCount: number = 0,
        forceRefresh: boolean = false
    ): Promise<CategoryNode[]> {
        if (this._isLoadingTree && retryCount === 0 && !forceRefresh) {
            if (this._bootstrapPromise) {
                await this._bootstrapPromise;
                return this.getCategoriesTreeSnapshot();
            }
            return (
                this._categoriesTree$.pipe(take(1)).toPromise() ||
                Promise.resolve([])
            );
        }

        if (forceRefresh) {
            this._isLoadingTree = false;
        }

        const loadContext = await this.resolveCategoriesLoadContext();
        const existingTree = this._categoriesTree$.getValue();
        const existingIndex = this._categoriesTreeIndex$.getValue();
        if (
            retryCount === 0 &&
            this.shouldUseCachedCategories(
                forceRefresh,
                loadContext.scopeKey,
                !!existingTree?.length
            )
        ) {
            const indexReady =
                !this.categoriesTreeResponseHasIndex || !!existingIndex;
            if (indexReady) {
                return existingTree;
            }
        }

        if (retryCount === 0) {
            this._categoriesClientScope = loadContext.scopeKey;
        }

        this._isLoadingTree = true;
        this._fuseLoadingService._setLoadingStatus(
            true,
            'premiums:categories:tree'
        );
        try {
            this._loading$.next(true);
            const cascadeOptions = this.buildPremiumCategoriesCascadeRequestOptions(
                loadContext
            );

            const result:
                | CategoryNode[]
                | CategoryNodeResponse
                | any = await this._brandfactoryServerlessService.listPremiumCategories(
                cascadeOptions
            );

            let treeArray: CategoryNode[] = [];
            let indexNode: CategoryNode | null = null;
            if (Array.isArray(result)) {
                treeArray = result;
                this.categoriesTreeResponseHasIndex = false;
            } else if (
                result &&
                typeof result === 'object' &&
                'data' in result
            ) {
                treeArray = result.data || [];
                indexNode = result.index || null;
                this.categoriesTreeResponseHasIndex = true;
            }

            if (loadContext.applyRestrictedFilter) {
                const snapshotCategories = this.getCategoriesSnapshot();
                const categoriesForFilter =
                    this.categories.length > 0
                        ? this.categories
                        : snapshotCategories.length > 0
                        ? snapshotCategories
                        : ((await this._brandfactoryServerlessService.listPremiumCategories(
                              this.buildPremiumCategoriesRequestOptions(
                                  loadContext
                              )
                          )) as Category[]) || [];

                const filtered = this.applyRestrictedCategoryFilters(
                    categoriesForFilter,
                    treeArray,
                    true
                );
                treeArray = filtered.tree;
                if (!this.categories.length) {
                    this.categories = filtered.categories;
                    this._categories$.next(
                        sortJSON(this.categories, 'name', 'asc')
                    );
                }
                if (
                    indexNode?.id &&
                    this.getRestrictedCategoryIds(categoriesForFilter).has(
                        indexNode.id
                    )
                ) {
                    indexNode = null;
                }
            }

            if (!treeArray || treeArray.length === 0) {
                if (retryCount < this._maxRetries) {
                    this._isLoadingTree = false;
                    await new Promise(resolve =>
                        setTimeout(resolve, this._retryDelay)
                    );
                    return this.listCategoriesTree(retryCount + 1);
                }
                this.categoriesTree = [];
            } else {
                this.categoriesTree = treeArray;
            }
            this._categoriesTreeIndex$.next(indexNode);
        } catch (err) {
            if (retryCount < this._maxRetries) {
                this._isLoadingTree = false;
                await new Promise(resolve =>
                    setTimeout(resolve, this._retryDelay)
                );
                return this.listCategoriesTree(retryCount + 1);
            }
            this.categoriesTree = [];
            this._categoriesTreeIndex$.next(null);
        } finally {
            this._categoriesTree$.next(this.categoriesTree);
            this._loading$.next(false);
            this._isLoadingTree = false;
            this._fuseLoadingService._setLoadingStatus(
                false,
                'premiums:categories:tree'
            );
        }

        return this.categoriesTree;
    }

    async findCategoryById(id: string): Promise<Category | undefined> {
        try {
            const categories = await this.categories$.pipe(take(1)).toPromise(); // Take 1 to unsubscribe after getting the value
            return categories.find(category => category.id === id);
        } catch (error) {
            console.error('Error finding category:', error);
            return undefined;
        }
    }

    async findCategoryByRoute(route: string): Promise<Category | undefined> {
        try {
            const categories = await this.categories$.pipe(take(1)).toPromise(); // Take 1 to unsubscribe after getting the value
            return categories.find(category => category.path === route);
        } catch (error) {
            console.error('Error finding category:', error);
            return undefined;
        }
    }
}
